import React from 'react';
import {
	Box,
	Stack,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Typography,
	Switch,
	Tooltip,
} from '@mui/material';
import LinkButton from 'common/components/LinkButton';
import { Routes } from 'routing';
import { CurrencyObject } from 'common/types';
import { ProductVariant } from 'common/modules/inventory';
import MoneyTextField from 'common/components/MoneyTextField';
import produce from 'immer';
import { getVariantName } from 'common/utils/productUtils';
import { RiQuestionLine } from 'react-icons/ri';
import { getPricingString } from 'common/utils/common';
import { useTranslation } from 'services/localization';

export interface VariantRentalPriceTableProps {
	variants: ProductVariant[];
	onVariantsChange: (value: ProductVariant[]) => void;
	productId: string;
	currency: CurrencyObject;
	disabled?: boolean;
}
const VariantRentalPriceTable = (props: VariantRentalPriceTableProps) => {
	const { t } = useTranslation();
	const { productId, variants, onVariantsChange, currency, disabled } = props;

	const handleVariantEnabledChange = (index: number) => (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const newVariants = produce(variants, (draft) => {
			const variant = draft[index];
			if (!!variant.rentals) {
				variant.rentals.enabled = event.target.checked;
			} else {
				variant.rentals = {
					enabled: event.target.checked,
					priceIncrease: null,
				};
			}
		});

		onVariantsChange(newVariants);
	};
	const handleVariantPriceChange = (index: number) => (price: number) => {
		const newVariants = produce(variants, (draft) => {
			draft[index].rentals = {
				enabled: true,
				priceIncrease: price,
			};
		});

		onVariantsChange(newVariants);
	};

	return (
		<Box>
			<Stack direction="row" justifyContent="space-between" spacing={2}>
				<Typography
					variant="h6"
					sx={{
						opacity: disabled ? 0.3 : 1,
					}}
				>
					{t('VariantRentalPriceTable.variantPricing', 'Variant pricing')}
				</Typography>
				<LinkButton
					color="primary"
					to={Routes.ProductPageTab(productId, 'variants')}
					disabled={disabled}
				>
					{t('VariantRentalPriceTable.editVariants', 'Edit variants')}
				</LinkButton>
			</Stack>
			<Table sx={{ mt: 2 }}>
				<TableHead>
					<TableRow sx={{ opacity: disabled ? 0.3 : 1 }}>
						<TableCell>{t('product.variant', 'Variant')}</TableCell>
						<TableCell>
							{t('product.variantPriceIncrease', 'Price increase')}
							<Tooltip title="An additional price per variant, which is added on top of the normal booking price">
								<Box
									display="inline"
									sx={{ ml: 0.5, color: (theme) => theme.palette.colors.tundra.main }}
								>
									<RiQuestionLine size={20} />
								</Box>
							</Tooltip>
						</TableCell>
						<TableCell align="right">
							{t('VariantRentalPriceTable.forRent', 'For rent')}
							<Tooltip
								title={t(
									'VariantRentalPriceTable.canThisVariantBeBooked',
									'Can this variant be booked?',
								)}
							>
								<Box
									display="inline"
									sx={{ ml: 0.5, color: (theme) => theme.palette.colors.tundra.main }}
								>
									<RiQuestionLine size={20} />
								</Box>
							</Tooltip>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{variants.map((variant, index) => (
						<TableRow key={variant.id}>
							<TableCell
								sx={{
									opacity: disabled ? 0.3 : 1,
								}}
							>
								{getVariantName(Object.values(variant.values), 'en')}
							</TableCell>
							<TableCell>
								<MoneyTextField
									label=""
									variant="outlined"
									value={variant.rentals?.priceIncrease}
									onValueChange={handleVariantPriceChange(index)}
									disabled={disabled || !variant.rentals?.enabled}
									placeholder={
										variant.rentals?.enabled
											? getPricingString(0, currency)
											: t('VariantRentalPriceTable.notForRent', 'Not for rent')
									}
									currency={currency}
									useMinorUnits
								/>
							</TableCell>
							<TableCell align="right">
								<Switch
									disabled={disabled}
									checked={variant.rentals?.enabled ?? false}
									onChange={handleVariantEnabledChange(index)}
								/>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);
};

export default VariantRentalPriceTable;
